import React from "react";

const STATUS = {
  PROGRAMADA: "PROGRAMADA",
  PENDENTE: "PENDENTE",
  ATRASADA: "ATRASADA",
  APLICADA: "APLICADA",
};

function safeNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function StatusPill({ status }) {
  const statusMap = {
    ATRASADA: {
      label: "Atrasada",
      className: "border-red-200 bg-red-50 text-red-700 animate-pulse",
    },
    PENDENTE: {
      label: "Pendente",
      className: "border-amber-200 bg-amber-50 text-amber-700",
    },
    PROGRAMADA: {
      label: "Programada",
      className: "border-indigo-200 bg-indigo-50 text-indigo-700",
    },
    APLICADA: {
      label: "Realizada",
      className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    },
  };

  const current = statusMap[status] || {
    label: status || "Status",
    className: "border-gray-200 bg-gray-50 text-gray-600",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider ${current.className}`}
    >
      {current.label}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="glass-card rounded-3xl p-8 text-center border border-white/80 shadow-md">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-100/80 text-purple-700 text-xl font-bold mb-3">
        📅
      </div>
      <h4 className="text-sm font-bold text-[#2A1F45]">
        Você não tem doses ativas no momento.
      </h4>
      <p className="mt-1 text-xs text-gray-500 font-medium">
        Toque em “Planejar Novas Doses” para agendar seus medicamentos.
      </p>
    </div>
  );
}

function DoseMessage({ message }) {
  if (!message?.text) return null;

  const className =
    message.type === "error"
      ? "border-red-200 bg-red-50 text-red-700"
      : message.type === "warning"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : "border-emerald-200 bg-emerald-50 text-emerald-700";

  return (
    <div
      className={`mt-3 rounded-2xl border px-3.5 py-2.5 text-xs font-semibold ${className}`}
    >
      {message.text}
    </div>
  );
}

export default function DoseActiveList({
  doses = [],
  getId,
  getDoseFlask,
  formatDateTimeBR,
  formatPtNumber,
  formatDelay,
  applyDose,
  markSkipped,
  openEdit,
  deleteDose,
  doseMessages = {},
}) {
  if (!doses.length) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-3.5">
      {doses.map((dose) => {
        const id = getId(dose);
        const status = String(
          dose?.__derivedStatus || dose?.status || ""
        ).toUpperCase();

        const canApply =
          status === STATUS.PROGRAMADA ||
          status === STATUS.PENDENTE ||
          status === STATUS.ATRASADA;

        const flask = getDoseFlask(dose);
        const medName = flask?.name || dose?.medicationName || "Medicamento";
        const brand = flask?.brand || flask?.packagingLabel || "Medicamento";

        const medType = String(flask?.medicationType || "").toUpperCase();
        const nameLower = String(medName).toLowerCase();
        const unitLabel = String(flask?.unitLabel || "").toLowerCase();
        const mgPerUnit = safeNumber(flask?.mgPerUnit ?? flask?.mgBase);

        let actionLabel = "Registrar Dose Tomada";
        let iconEmoji = "💊";
        let dosageLabel = `${formatPtNumber(dose?.dosage, 2)} mg`;

        if (medType === "INJETAVEL") {
          actionLabel = "Registrar Aplicação";
          iconEmoji = "💉";
          dosageLabel = `${formatPtNumber(dose?.dosage, 2)} mg`;
        } else if (
          medType === "PO" ||
          medType === "PÓ" ||
          nameLower.includes("glutamina") ||
          nameLower.includes("creatina") ||
          nameLower.includes("whey") ||
          nameLower.includes("colageno")
        ) {
          actionLabel = "Registrar Consumo";
          iconEmoji = "🥤";
          if (mgPerUnit && dose?.dosage) {
            const scoops = Math.round(dose.dosage / mgPerUnit) || 1;
            dosageLabel = `${scoops} dose(s)`;
          } else {
            dosageLabel = "1 dose";
          }
        } else if (medType === "COMPRIMIDO" || medType === "CAPSULA") {
          actionLabel = "Registrar Dose Tomada";
          iconEmoji = "💊";

          if (mgPerUnit && dose?.dosage) {
            const count = Math.round(dose.dosage / mgPerUnit) || 1;
            dosageLabel = `${count} ${medType === "CAPSULA" ? "cápsula(s)" : "comprimido(s)"}`;
          } else if (unitLabel === "mcg" || (dose?.dosage > 0 && dose?.dosage < 1)) {
            const mcgVal = dose.dosage < 1 ? Math.round(dose.dosage * 1000 * 100) / 100 : dose.dosage;
            dosageLabel = `${formatPtNumber(mcgVal, 2)} mcg`;
          }
        }

        const doseMessage = doseMessages?.[String(id)];

        return (
          <article
            key={id}
            className={`glass-card glass-card-hover rounded-3xl p-4 sm:p-5 transition-all duration-300 ${
              status === STATUS.ATRASADA ? "border-l-4 border-l-red-500" : ""
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-purple-100/80 text-purple-700 text-xl font-bold border border-purple-200/50">
                  {iconEmoji}
                </div>

                <div className="min-w-0">
                  <h4 className="text-base font-extrabold text-[#2A1F45] truncate">
                    {medName}
                  </h4>
                  <p className="text-xs font-medium text-gray-500 truncate">
                    {brand}
                  </p>
                </div>
              </div>

              <StatusPill status={status} />
            </div>

            <div className="mt-3.5 pt-3 border-t border-purple-100/60 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-gray-800">
                  {formatDateTimeBR(dose?.date, dose?.scheduleTime)}
                </p>
                {status === STATUS.ATRASADA && (
                  <p className="text-[11px] font-extrabold text-red-600 mt-0.5">
                    {formatDelay(dose?.date)}
                  </p>
                )}
              </div>

              <span className="inline-flex items-center rounded-xl bg-purple-50 px-3 py-1 text-xs font-extrabold text-purple-800 border border-purple-100">
                {dosageLabel}
              </span>
            </div>

            <DoseMessage message={doseMessage} />

            <div className="mt-4 space-y-2">
              {canApply && (
                <button
                  type="button"
                  onClick={() => applyDose(dose)}
                  className="w-full rounded-2xl bg-gradient-to-r from-[#4A3B75] to-[#6A51AD] hover:from-[#3B2D60] hover:to-[#594396] active:scale-95 text-white py-3 text-xs font-extrabold uppercase tracking-wider shadow-md shadow-purple-200 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  {actionLabel}
                </button>
              )}

              {canApply && (
                <button
                  type="button"
                  onClick={() => markSkipped(dose)}
                  className="w-full rounded-2xl border border-purple-200/80 bg-white hover:bg-purple-50 active:scale-95 text-purple-800 py-2.5 text-xs font-bold uppercase tracking-wider shadow-sm transition-all duration-200 cursor-pointer"
                >
                  Pular Dose
                </button>
              )}
            </div>

            <div className="mt-3.5 pt-2 flex items-center justify-end gap-4 text-xs font-bold">
              <button
                type="button"
                onClick={() => openEdit(dose)}
                className="text-gray-500 hover:text-purple-800 transition-colors cursor-pointer"
              >
                Editar
              </button>

              <button
                type="button"
                onClick={() => deleteDose(dose)}
                className="text-red-500 hover:text-red-700 transition-colors cursor-pointer"
              >
                Excluir
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}